import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";

import "./index.css";
import App from "./App";
import ApiClient from "./api/generated/src/ApiClient";
import { shouldSkipApiErrorEvent } from "./api/sentryApiErrorPolicy";
import { initApiSentryInterceptor, isCompanyAlreadyMatchedException } from "./api/sentryApiInterceptor";
import {
  hasSensitiveUrlParam,
  scrubReplayRecordingEvent,
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from "./api/sentryScrub";
import { initializeAccessToken } from "./auth/accessToken";

import reportWebVitals from "./reportWebVitals";
import { isDeployBundleMismatchError } from "./utils/deployErrorDetector";

if (process.env.REACT_APP_ENABLE_SENTRY === "true") {
  Sentry.init({
    dsn: "https://7b6c33ad73a043719d15ad78e1741ef2@o561825.ingest.us.sentry.io/6629470",
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ["error", "warn"] }),
      // 画面の文字・入力値・画像を録画に残さない（SDK の既定値だが、既定値が変わっても緩まないよう明示する）
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // 録画に残る画面遷移の URL のトークン（?prt= 等）を伏せる
        beforeAddRecordingEvent: (event) => scrubReplayRecordingEvent(event),
        // 録画冒頭のページ URL は SDK の仕組み上伏せられないため、トークン付きの画面（パスワード再設定等）でのエラーは録画しない
        beforeErrorSampling: () => !hasSensitiveUrlParam(window.location.href),
      }),
    ],
    // Session Replay: エラー発生時のみ記録（通常セッションは記録しない）
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // console のパンくずの引数（API エラーの本文・応答）と、画面遷移・通信の URL のトークンを伏せる。
    // Session Replay はこれを通った後のパンくずを録画に入れる
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    beforeSend(event, hint) {
      if (shouldSkipApiErrorEvent(hint)) return null;
      const error = hint?.originalException;
      // 401エラーで、セッション確認APIの場合は除外
      if (error && typeof error === "object" && "status" in error && error.status === 401) {
        return null;
      }
      // 会社マッチ済み 422 は画面で理由を案内済みの想定内の応答。API 呼び出し側の console.error 経由でも
      // 届くため、interceptor の除外（shouldSkipSentryReport）とは別にここでも落とす
      if (isCompanyAlreadyMatchedException(error)) {
        return null;
      }
      // hint.originalException が null の場合event.exception の値をフォールバックとして使用する
      const exceptionValue = event.exception?.values?.[0];
      // RangeError (Maximum call stack size exceeded) はUI影響なし
      if (
        exceptionValue?.type === "RangeError" &&
        exceptionValue?.value?.includes("Maximum call stack size exceeded")
      ) {
        return null;
      }
      const errorLike = error ?? `${exceptionValue?.type ?? ""}: ${exceptionValue?.value ?? ""}`;
      // デプロイ起因エラーはタグなしの生エラーを除外
      if (isDeployBundleMismatchError(errorLike) && event.tags?.error_cause !== "deploy_bundle_mismatch") {
        return null;
      }
      // デプロイ起因エラーはタイトルを書き換えて送信
      if (event.tags?.error_cause === "deploy_bundle_mismatch") {
        const agentId = event.user?.id ?? "unknown";
        if (event.exception?.values?.[0]) {
          event.exception.values[0].type = "DeployBundleMismatchError";
          event.exception.values[0].value = `[デプロイ起因] AgentID:${agentId} ブラウザに古いバンドルが残っています。自動リロードを試みます。`;
        }
      }
      // console.error(error) 経由の superagent エラーの本文・応答と、画面 URL のトークン（?prt= 等）を伏せる
      return scrubSentryEvent(event);
    },
  });
  // Session Replay のイベント（replay_event）は beforeSend を通らないため、訪問 URL の一覧はイベントプロセッサで伏せる
  Sentry.addEventProcessor((event) => (event.type === "replay_event" ? scrubSentryEvent(event) : event));
}

initApiSentryInterceptor();
ApiClient.instance.timeout = 120000; // タイムアウトの時間をデフォルト60秒から2分に延長

const renderApp = () => {
  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

initializeAccessToken()
  .catch(() => {
    // 認証初期化失敗時もアプリは起動する
  })
  .finally(() => {
    renderApp();
  });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
